import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { RefreshCw, Users as UsersIcon, ShoppingBag, SlidersHorizontal } from 'lucide-react';
import { api, errorMessage, toPage } from '../services/api';
import ConfirmationPopup from '../components/ConfirmationPopup';
import {
  PageHeader,
  Card,
  Segmented,
  SearchInput,
  Pagination,
  Sheet,
  Button,
  Badge,
  CopyButton,
  InfoRow,
  Avatar,
  Field,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  inputClass,
  useDebounce
} from '../components/ui';
import { formatCompactCurrency, formatCurrency, formatDateTime, formatNumber, formatRelative } from '../utils/format';

const BALANCE_TABS = [
  { value: 'all', label: 'Tất cả' },
  { value: 'has_balance', label: 'Có số dư' },
  { value: 'no_balance', label: 'Không số dư' }
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Mới đăng ký' },
  { value: 'approved_desc', label: 'Số dư rút được cao nhất' },
  { value: 'pending_desc', label: 'Số dư chờ cao nhất' },
  { value: 'withdrawn_desc', label: 'Đã rút nhiều nhất' },
  { value: 'last_login', label: 'Đăng nhập gần đây' }
];

const UsersPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [balance, setBalance] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selected, setSelected] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const debouncedSearch = useDebounce(search);
  const debouncedUserId = useDebounce(userId);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['users', debouncedSearch, debouncedUserId, balance, status, sort, page, limit],
    async () => {
      const params = { page, limit, sort };
      if (debouncedSearch) params.search = debouncedSearch;
      if (debouncedUserId) params.user_id = debouncedUserId;
      if (balance !== 'all') params.balance = balance;
      if (status !== 'all') params.status = status;
      return toPage(await api.get('/admin/users', { params }));
    },
    { keepPreviousData: true }
  );

  const { data: stats } = useQuery('users-stats', async () => (await api.get('/admin/user-stats')).data.data, { staleTime: 60000 });

  const users = data?.items || [];
  const pagination = data?.pagination || {};
  const filterCount = (status !== 'all' ? 1 : 0) + (sort !== 'newest' ? 1 : 0) + (userId ? 1 : 0);

  const recalcOne = async (user) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/balance/recalculate`);
      toast.success('Đã tính lại số dư');
      const res = await refetch();
      const fresh = res.data?.items?.find((u) => u.id === user.id);
      if (fresh) setSelected(fresh);
      queryClient.invalidateQueries('users-stats');
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi tính lại số dư'));
    } finally {
      setBusy(false);
    }
  };

  const recalcAll = async () => {
    setBusy(true);
    try {
      const res = await api.post('/admin/balances/recalculate');
      const r = res.data?.data?.results;
      toast.success(r ? `Đã tính lại ${r.ok}/${r.total} user${r.fail ? `, lỗi ${r.fail}` : ''}` : 'Đã tính lại toàn bộ số dư');
      setConfirmAll(false);
      refetch();
      queryClient.invalidateQueries('users-stats');
      queryClient.invalidateQueries('dashboard');
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi tính lại toàn bộ số dư'));
    } finally {
      setBusy(false);
    }
  };

  const balanceCells = (u) => (
    <div className="grid grid-cols-3 gap-2 text-xs">
      <div>
        <p className="text-gray-500">Rút được</p>
        <p className={Number(u.balance?.approved_balance) < 0 ? 'font-semibold text-red-600' : 'font-semibold text-emerald-700'}>
          {formatCompactCurrency(u.balance?.approved_balance)}
        </p>
      </div>
      <div>
        <p className="text-gray-500">Đang chờ</p>
        <p className="font-semibold text-amber-700">{formatCompactCurrency(u.balance?.pending_balance)}</p>
      </div>
      <div className="text-right">
        <p className="text-gray-500">Đã rút</p>
        <p className="font-semibold text-gray-900">{formatCompactCurrency(u.balance?.total_withdrawn)}</p>
      </div>
    </div>
  );

  const renderCard = (u) => (
    <Card as="button" type="button" onClick={() => setSelected(u)} className="w-full p-4 text-left active:bg-gray-50">
      <div className="flex items-center gap-3">
        <Avatar name={u.name} email={u.email} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{u.name || 'Chưa có tên'}</p>
          <p className="truncate text-xs text-gray-500">{u.email}</p>
        </div>
        {!u.is_active ? <Badge tone="red">Khóa</Badge> : u.email_verified ? <Badge tone="green">Đã xác thực</Badge> : <Badge>Chưa xác thực</Badge>}
      </div>
      <div className="mt-3 border-t border-gray-100 pt-3">{balanceCells(u)}</div>
      <p className="mt-2 text-xs text-gray-400">Đăng nhập {formatRelative(u.last_login_at)} · tham gia {formatRelative(u.created_at)}</p>
    </Card>
  );

  const columns = [
    {
      key: 'user',
      title: 'Người dùng',
      render: (u) => (
        <div className="flex items-center gap-3">
          <Avatar name={u.name} email={u.email} />
          <div className="min-w-0">
            <p className="font-medium text-gray-900">{u.name || 'Chưa có tên'}</p>
            <p className="text-xs text-gray-500">{u.email}</p>
          </div>
        </div>
      )
    },
    { key: 'approved', title: 'Rút được', align: 'right', render: (u) => <span className="font-medium text-emerald-700">{formatCurrency(u.balance?.approved_balance)}</span> },
    { key: 'pending', title: 'Đang chờ', align: 'right', render: (u) => <span className="text-amber-700">{formatCurrency(u.balance?.pending_balance)}</span> },
    { key: 'withdrawn', title: 'Đã rút', align: 'right', render: (u) => formatCurrency(u.balance?.total_withdrawn) },
    { key: 'status', title: 'Trạng thái', render: (u) => (!u.is_active ? <Badge tone="red">Khóa</Badge> : u.email_verified ? <Badge tone="green">Đã xác thực</Badge> : <Badge>Chưa xác thực</Badge>) },
    { key: 'login', title: 'Đăng nhập', render: (u) => <span className="text-xs text-gray-500">{formatRelative(u.last_login_at)}</span> }
  ];

  const ov = stats?.overview;
  const bs = stats?.balanceStats;

  return (
    <div>
      <PageHeader
        title="Người dùng"
        subtitle={ov ? `${formatNumber(ov.totalUsers)} người dùng · ${formatNumber(ov.activeUsers)} có đơn trong 30 ngày` : undefined}
        actions={
          <Button variant="outline" onClick={() => setConfirmAll(true)}>
            <RefreshCw className="h-4 w-4" /> Tính lại toàn bộ số dư
          </Button>
        }
      />

      {stats && (
        <div className="mb-4 grid grid-cols-3 gap-3">
          <Card className="p-3">
            <p className="text-xs text-gray-500">Rút được</p>
            <p className="tabular mt-0.5 font-semibold text-gray-900">{formatCompactCurrency(bs?.totalApproved)}</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-gray-500">Đang chờ</p>
            <p className="tabular mt-0.5 font-semibold text-gray-900">{formatCompactCurrency(bs?.totalPending)}</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-gray-500">Đã rút</p>
            <p className="tabular mt-0.5 font-semibold text-gray-900">{formatCompactCurrency(bs?.totalWithdrawn)}</p>
          </Card>
        </div>
      )}

      <div className="filter-bar">
        <div className="flex gap-2 lg:min-w-[18rem] lg:flex-1">
          <SearchInput className="flex-1" value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Tìm theo email hoặc tên" />
          <Button variant={filterCount ? 'soft' : 'outline'} size="icon" className="relative h-11 w-11 flex-shrink-0 md:h-9 md:w-9" onClick={() => setShowFilters(true)} aria-label="Bộ lọc">
            <SlidersHorizontal className="h-4 w-4" />
            {filterCount > 0 && <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-900 text-[11px] text-white">{filterCount}</span>}
          </Button>
        </div>
        <Segmented options={BALANCE_TABS} value={balance} onChange={(v) => { setBalance(v); setPage(1); }} />
      </div>

      <ErrorBanner message={error ? 'Lỗi khi tải danh sách người dùng' : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : (
        <ResponsiveList
          items={users}
          renderCard={renderCard}
          columns={columns}
          onRowClick={setSelected}
          empty={<EmptyState icon={UsersIcon} title="Không có người dùng phù hợp" />}
        />
      )}

      {pagination.total > 0 && (
        <Pagination
          page={page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          onChange={setPage}
          limit={limit}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
          disabled={isFetching}
        />
      )}

      {/* Bộ lọc nâng cao */}
      <Sheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title="Lọc & sắp xếp"
        footer={
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => { setStatus('all'); setSort('newest'); setUserId(''); setPage(1); }}>Đặt lại</Button>
            <Button onClick={() => setShowFilters(false)}>Xong</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label="Sắp xếp">
            <select className={inputClass} value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Xác thực email">
            <Segmented
              value={status}
              onChange={(v) => { setStatus(v); setPage(1); }}
              options={[
                { value: 'all', label: 'Tất cả' },
                { value: 'verified', label: 'Đã xác thực' },
                { value: 'unverified', label: 'Chưa xác thực' }
              ]}
            />
          </Field>
          <Field label="User ID (một phần)">
            <input className={inputClass} value={userId} onChange={(e) => { setUserId(e.target.value.trim()); setPage(1); }} placeholder="VD: 3f2a9c" />
          </Field>
        </div>
      </Sheet>

      {/* Chi tiết user */}
      <Sheet
        open={!!selected}
        onClose={() => !busy && setSelected(null)}
        title={selected?.name || selected?.email || ''}
        footer={selected && (
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => recalcOne(selected)} loading={busy}>
              <RefreshCw className="h-4 w-4" /> Tính lại số dư
            </Button>
            <Link
              to={`/orders?user_id=${selected.id}`}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 md:h-9 text-sm font-medium text-white hover:bg-gray-800"
            >
              <ShoppingBag className="h-4 w-4" /> Xem đơn hàng
            </Link>
          </div>
        )}
      >
        {selected && (
          <div className="space-y-4">
            <Card className="p-4">{balanceCells(selected)}</Card>
            <Card className="divide-y divide-gray-100 px-4">
              <InfoRow label="Email">{selected.email}</InfoRow>
              <InfoRow label="User ID">
                <span className="inline-flex items-center gap-1 font-mono text-xs">
                  {selected.id}
                  <CopyButton value={selected.id} />
                </span>
              </InfoRow>
              <InfoRow label="Vai trò">{selected.role}</InfoRow>
              <InfoRow label="Xác thực">{selected.email_verified ? `Có (${formatDateTime(selected.email_verified_at)})` : 'Chưa'}</InfoRow>
              <InfoRow label="Hoạt động">{selected.is_active ? 'Đang hoạt động' : 'Bị khóa'}</InfoRow>
              <InfoRow label="Đăng nhập cuối">{formatDateTime(selected.last_login_at)}</InfoRow>
              <InfoRow label="Đăng ký">{formatDateTime(selected.created_at)}</InfoRow>
              <InfoRow label="Tính số dư lúc">{formatDateTime(selected.balance?.last_calculated)}</InfoRow>
            </Card>
          </div>
        )}
      </Sheet>

      <ConfirmationPopup
        isOpen={confirmAll}
        onClose={() => !busy && setConfirmAll(false)}
        onConfirm={recalcAll}
        title="Tính lại toàn bộ số dư"
        message="Tính lại số dư cho tất cả người dùng từ đơn hàng và lệnh rút. Có thể mất vài phút với nhiều người dùng."
        confirmText="Tính lại"
        type="warning"
        isLoading={busy}
      />
    </div>
  );
};

export default UsersPage;
